import { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { Pct, fmtPrice } from "@/components/layernav/primitives";
import { detectAssets } from "@/components/article/MarketContext";

const ASSET_FAMILIES = ["ASSET_MARKET", "INSTITUTIONAL", "RWA", "PROTOCOL"];

/* Story Intelligence strip (§14-15): verified NewsLayer data only.
   Blocks with no truthful value are omitted — never faked to fill the layout. */
export const IntelStrip = ({ article }: { article: Article }) => {
  const ctx: any = article.context;
  const family = ctx?.family || "GENERAL";
  const sym = ctx?.asset || ctx?.assets_detected?.[0] || detectAssets(article)[0] || null;
  const wantCoin = !!sym && (ASSET_FAMILIES.includes(family) || family === "GENERAL" || family === "SECTOR");

  const { data: markets } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    staleTime: 90 * 1000,
    enabled: wantCoin,
  });
  const { data: global } = useQuery({
    queryKey: ["markets-global"],
    queryFn: async () => (await api.get("/markets/global")).data,
    staleTime: 10 * 60 * 1000,
    enabled: family === "GLOBAL_MARKET",
  });

  const facts = ((article as any).story_snapshot || []) as { value: string; label: string }[];
  const entities = (article.entities || []).slice(0, 4);
  const coin = wantCoin ? (markets?.coins || []).find((c: any) => c.symbol === sym) : null;

  const blocks: { label: string; node: ReactNode; testid: string }[] = [];
  if (facts.length) {
    blocks.push({
      label: "Key Signal", testid: "intel-key-signal",
      node: (
        <p className="text-[13px] leading-snug text-white">
          <span className="font-bold">{facts[0].value}</span>{" "}
          <span className="text-gray-400">{facts[0].label}</span>
        </p>
      ),
    });
  }
  if (entities.length) {
    blocks.push({
      label: "Entities", testid: "intel-entities",
      node: <p className="text-[13px] leading-snug text-gray-200">{entities.join(", ")}</p>,
    });
  }
  if (coin) {
    blocks.push({
      label: "Market Impact", testid: "intel-market-impact",
      node: (
        <p className="font-mono text-[13px] text-white flex items-baseline gap-2">
          <span className="font-bold">{coin.symbol}</span>
          <Pct v={coin.change_24h} className="text-[12px]" />
          <span className="text-gray-500 text-[11px]">{fmtPrice(coin.price)}</span>
        </p>
      ),
    });
  } else if (family === "GLOBAL_MARKET" && typeof global?.market_cap_change_24h_pct === "number") {
    blocks.push({
      label: "Market Impact", testid: "intel-market-impact",
      node: (
        <p className="font-mono text-[13px] text-white flex items-baseline gap-2">
          <span className="text-gray-400 text-[11px] uppercase">Total MCap</span>
          <Pct v={global.market_cap_change_24h_pct} className="text-[12px]" />
        </p>
      ),
    });
  }
  const stamp = (article as any).updated_at || article.published_at;
  if (stamp) {
    blocks.push({
      label: "Last Updated", testid: "intel-last-updated",
      node: <p className="text-[13px] text-gray-200">{timeAgo(stamp)}</p>,
    });
  }

  if (blocks.length < 2) return null;

  return (
    <section data-testid="story-intel-strip" aria-label="Story intelligence"
      className={`mt-5 rounded-xl border border-white/[0.08] bg-[#0a0b10] grid grid-cols-2 ${
        blocks.length >= 4 ? "lg:grid-cols-4" : blocks.length === 3 ? "lg:grid-cols-3" : ""}`}>
      {blocks.map((b, i) => (
        <div key={b.label} data-testid={b.testid}
          className={`px-4 py-3.5 min-w-0 ${i % 2 === 1 ? "border-l border-white/[0.06]" : ""} ${
            i >= 2 ? "border-t border-white/[0.06] lg:border-t-0" : ""} ${
            i >= 1 ? "lg:border-l lg:border-white/[0.06]" : ""}`}>
          <p className="font-mono text-[8.5px] font-bold uppercase tracking-[0.24em] text-[#A78BFA]">{b.label}</p>
          <div className="mt-1.5">{b.node}</div>
        </div>
      ))}
    </section>
  );
};
